import { get, isEqual } from 'lodash'

import { Services, getServicePathToAPI, joinURLPath } from '@sigma-frontend-libs/commons'
import { Macro } from '@sigma-legacy-libs/cache'

import { getSocket, projectName, request } from '@/utils'

import { macroCacheOptions } from '../constants'
import { globalErrorHandler } from '../request/error'

const Cache = new Macro(macroCacheOptions)
const overdraftSettingKey = `${Services.billings}.overdraft`
const billingsPathToAPI = getServicePathToAPI(Services.billings)
const settingsUserPathToAPI = getServicePathToAPI(Services.settingsUser)

export const balance = (options = {}) => {
  return {
    props: {
      UserId: String,
      currency: String
    },

    data() {
      return {
        balance: 0,
        balanceLoading: false,

        overdraft: 0,
        overdraftLoading: false,

        canReadOverdraft: true
      }
    },

    computed: {
      $UserId() {
        return this.UserId || options.UserId
      }
    },

    watch: {
      $route: {
        handler(to, from) {
          if (!isEqual(to.params, from.params) || to.name !== from.name) {
            this.getBalance()
            this.getOverdraft()
          }
        },
        deep: true
      }
    },

    mounted() {
      if (!this.$UserId || this.$UserId === this.account.id) {
        getSocket()?.on(`${Services.billings}.created`, ({ OwnerId }) => {
          if (this.account.id === OwnerId) {
            this.getBalance()
          }
        })
      }

      this.getBalance()
      this.getOverdraft()
    },

    methods: {
      cachedGetBalance: Cache.wrapWithCache(async (key, UserId) => {
        return await request.get(joinURLPath(billingsPathToAPI, 'balance', UserId))
      }),
      cachedGetOverdraftKeys: Cache.wrapWithCache(async (key, UserId) => {
        return await request.get(joinURLPath(settingsUserPathToAPI, 'keys', UserId))
      }),
      cachedGetOverdraft: Cache.wrapWithCache(async (key, UserId) => {
        return await request.get(joinURLPath(settingsUserPathToAPI, UserId, overdraftSettingKey))
      }),

      async getBalance() {
        try {
          this.balanceLoading = true
          const UserId = this.$UserId || this.account.id
          const key = [ projectName, Services.billings, 'balance', UserId ].filter(Boolean).join(':')
          const data = await this.cachedGetBalance(key, UserId)
          if (data) {
            this.balance = data.result
          }
        } catch (error) {
          globalErrorHandler(error)
        } finally {
          this.balanceLoading = false
        }
      },

      async canReadOverdraftSetting(UserId) {
        try {
          const key = [ projectName, Services.settingsUser, 'get', 'keys', UserId ].filter(Boolean).join(':')
          const data = await this.cachedGetOverdraftKeys(key, UserId)
          const overdraftAccess = get(data, overdraftSettingKey)

          return [ 'read', 'write' ].includes(overdraftAccess)
        } catch (error) {
          return this.checkPermissions(`advanced.${Services.settingsUser}.get`)
        }
      },
      async getOverdraft() {
        if (this.$UserId) {
          this.canReadOverdraft = await this.canReadOverdraftSetting(this.$UserId)
          if (this.canReadOverdraft) {
            try {
              this.overdraftLoading = true
              const key = [ projectName, Services.settingsUser, 'get', this.$UserId, overdraftSettingKey ].filter(Boolean).join(':')
              const data = await this.cachedGetOverdraft(key, this.$UserId)
              if (data) {
                let overdraft = parseFloat(data.result)
                if (isNaN(overdraft)) {
                  overdraft = 0
                }
                this.overdraft = overdraft
              }
            } catch (error) {
              globalErrorHandler(error)
            } finally {
              this.overdraftLoading = false
            }
          } else {
            this.overdraft = 0
          }
        } else {
          this.canReadOverdraft = true
          this.overdraft = get(this.account.settings, overdraftSettingKey, 0)
        }
      }
    }
  }
}
